import {
  ConsultantKarensItem,
  GetConsultantProfileNew,
  useSubmitConsultantKarensMutation
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { CirclePlusIcon, Color, Modal, useSnackbarsPipeline } from "@bonliva-crm/ui";
import { Button, Typography, makeStyles } from "@material-ui/core";
import { useState } from "react";
import { KarensCard } from "./components/karens-card";

type Props = {
  consultantId: string;
};

type ModalProps = Props & {
  open: boolean;
  setOpen: (b: boolean) => void;
  karens: ConsultantKarensItem[];
  setKarens?: (karens: ConsultantKarensItem[]) => void;
};

export const KarensModal = (props: ModalProps) => {
  const classes = useStyles();
  const [_karens, _setKarens] = useState<ConsultantKarensItem[]>(props.karens);
  const [submitConsultantKarensMutation] = useSubmitConsultantKarensMutation();
  const { showSuccess, showError } = useSnackbarsPipeline();

  const updateValues = (karens: ConsultantKarensItem, i: number) => {
    const newKarens = _karens.map((k, index) => {
      if (index === i) {
        return karens;
      }
      return k;
    });

    _setKarens(newKarens);
  };

  const addKarens = () => {
    const newKarens = [
      ..._karens,
      {
        region: "",
        expDate: null
      }
    ];

    _setKarens(newKarens);
  };

  const deleteKarens = (index: number) => {
    const newKarens = _karens.filter((_, i) => i !== index);
    _setKarens(newKarens);
  };

  const submitKarens = () => {
    submitConsultantKarensMutation({
      variables: {
        consultantId: props.consultantId,
        data: {
          karens: _karens.map((k) => ({
            __typename: "ConsultantKarensItem",
            region: k.region,
            expDate: k.expDate
          }))
        }
      },
      onCompleted: (d) => {
        showSuccess(locale.Updated);
        if (props.setKarens) props.setKarens(d.submitConsultantKarens.karens);
        _setKarens(d.submitConsultantKarens.karens);
        props.setOpen(false);
      },
      onError: (e) => {
        showError(locale.ErrorOccurred);
        console.error(e);
      },
      optimisticResponse: {
        submitConsultantKarens: {
          karens: _karens.map((k) => ({
            __typename: "ConsultantKarensItem",
            region: k.region,
            expDate: k.expDate
          }))
        }
      },
      refetchQueries: [GetConsultantProfileNew.OperationName]
    });
  };

  const renderModalContent = () => {
    return (
      <>
        {_karens.map((karens, index) => (
          <KarensCard
            key={index}
            karens={karens}
            index={index}
            updateValues={updateValues}
            deleteKarens={deleteKarens}
          />
        ))}
        <Button
          variant="text"
          color="primary"
          className={classes.addKarensButton}
          onClick={addKarens}
        >
          <CirclePlusIcon />
          <Typography variant="body1" color="primary">
            {locale.AddKarens}
          </Typography>
        </Button>
      </>
    );
  };

  return (
    <Modal
      onSubmit={submitKarens}
      open={props.open}
      onClose={() => {
        _setKarens(props.karens);
        props.setOpen(false);
      }}
      setOpen={props.setOpen}
      modalContent={renderModalContent()}
      saveButtonDisabled={
        _karens.some((k) => !k.region || !k.expDate) ||
        JSON.stringify(props.karens) === JSON.stringify(_karens)
      }
      dimensions={{
        maxWidth: "75%",
        width: "75%",
        height: "90%"
      }}
    />
  );
};

const useStyles = makeStyles(() => ({
  karensButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    boxShadow: "none",
    padding: 0,
    gap: "0.25rem"
  },
  addKarensButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    boxShadow: "none",
    "& span": {
      gap: "0.5rem"
    }
  },
  textWrapper: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: "0.25rem"
  },
  iconButton: {
    padding: 0
  },
  karensCardWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    backgroundColor: Color.OffWhite,
    borderRadius: "4px",
    border: `1px solid ${Color.LightGray}`,
    padding: "1rem",
    boxSizing: "border-box"
  },
  karensCardTopWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  karensCardInputs: {
    "& .react-datepicker__input-container": {
      "& input": {
        backgroundColor: `${Color.White} !important`
      }
    }
  }
}));
